import { createStyles, makeStyles } from "@material-ui/core";
import {
    AreaNotification,
    BenefitsView,
    Breakpoint,
    CompanyDetails,
    ContactView,
    InternalNotes,
    JobOverview as JobOverviewComponent,
    JobQualificationsView,
    LayoutContainer,
    RelodeTheme,
    RewardsView,
    textWithLinks,
    useDeviceSize,
    useGridStyles,
    useNotifications,
} from "@relode/core";
import classnames from "classnames";
import { format } from "date-fns";
import * as React from "react";

import { JobDetails } from "../../../../../shared/types";

interface JobOverviewProps {
    job: JobDetails;
}

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            detailsTwoColumn: {
                display: "grid",
                gridTemplateColumns: "65% 1fr [last-column]",
                columnGap: theme.spacing(6),
                rowGap: theme.spacing(3),
            },
            panelContainer: {
                width: "100%",
            },
            fitContent: {
                height: "fit-content",
            },
        }),
    { name: "JobOverview" }
);

export const JobOverview = (props: JobOverviewProps) => {
    const { job } = props;
    const { actions: notifications } = useNotifications();
    const classes = useStyles({});
    const gridStyles = useGridStyles({});
    const device = useDeviceSize();
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
        if (device === "mobile") {
            setIsMobile(true);
        }
    }, [device]);

    React.useEffect(() => {
        if (job.details.jobStatusMessage) {
            const message = !!job.details.statusReasonTimestamp
                ? `${job.details.jobStatusMessage} ${format(
                      job.details.statusReasonTimestamp,
                      "P p"
                  )}`
                : job.details.jobStatusMessage;
            notifications.showArea("info", message, "jobStatusMessage", {
                canClose: false,
            });
        } else {
            notifications.closeAreaNotification("jobStatusMessage");
        }

        if (
            !job.details?.teamHasActiveTeamPlan &&
            job.details?.jobStatus.isPausedOrClosedOrRequestedOrPending() &&
            job.details?.hasSignedAgreement &&
            !job.details?.jobAgreementHasActiveTeamPlan
        ) {
            notifications.showArea(
                "warning",
                "Your plan has expired. Please contact your job manager to update your plan.",
                "teamPlanWarning",
                { canClose: false }
            );
        } else if (
            job.details?.jobStatus.isPausedOrClosedOrRequestedOrPending() &&
            job.details?.hasSignedAgreement &&
            !job.details?.jobAgreementHasActiveTeamPlan
        ) {
            notifications.showArea(
                "warning",
                "The plan associated with this job has expired. Please contact your job manager to update your plan.",
                "teamPlanWarning",
                { canClose: false }
            );
        } else if (
            !job.details?.teamHasActiveTeamPlan &&
            job.details?.jobStatus.isPausedOrClosedOrRequestedOrPending()
        ) {
            notifications.showArea(
                "warning",
                "Your plan has expired. Please contact your job manager to update your plan.",
                "teamPlanWarning",
                { canClose: false }
            );
        }
    }, [job, notifications]);

    return (
        <LayoutContainer>
            <AreaNotification areaName="teamPlanWarning" />
            <AreaNotification areaName="jobStatusMessage" />
            <div
                className={
                    !isMobile
                        ? classnames(
                              classes.detailsTwoColumn,
                              classes.panelContainer
                          )
                        : null
                }
            >
                <div
                    className={classnames(
                        gridStyles.oneColumn,
                        classes.fitContent
                    )}
                >
                    <Breakpoint mobile>
                        <RewardsView
                            altTitle={"position"}
                            jobLocationName={job.details.location.locationName}
                            companyName={job.team.companyName}
                            openPositions={job.details.openPositionCount}
                            jobSalaryInUSDollars={job.details.salaryDescription}
                            jobTypeName={job.details.jobType.jobTypeName}
                            hoursPerWeek={job.details.hoursPerWeek}
                            contractStartDate={job.details.contractStartDate}
                            contractEndDate={job.details.contractEndDate}
                            jobPriorityTypeId={job.details.jobPriorityType?.id}
                            actionableLinks={false}
                        />
                    </Breakpoint>

                    <JobQualificationsView
                        qualifications={job.jobQualifications}
                    />

                    <JobOverviewComponent
                        broadcastDescription={textWithLinks(
                            job.details.overview
                        )}
                        primaryPosition={job.details.primaryJobPosition}
                        secondaryPositions={job.details.secondaryJobPositions}
                        jobAttachments={job.details.attachments}
                    />

                    {job.details.benefits.length > 0 ? (
                        <Breakpoint mobile>
                            <BenefitsView benefits={job.details.benefits} />
                        </Breakpoint>
                    ) : null}

                    <CompanyDetails
                        companyUrl={job.team.companyUrl}
                        companyName={job.team.companyName}
                        companyAddress1={job.team.companyAddress1}
                        companyAddress2={job.team.companyAddress2}
                        companyBackground={job.team.companyBackground}
                        companyLocationName={job.team.companyLocationName}
                        actionableLinks={true}
                    />

                    <Breakpoint mobile>
                        <ContactView
                            label={"job manager"}
                            jobId={job.details.jobId}
                            jobName={job.details.jobName}
                            contactName={job.details.jobManager.fullName}
                            contactUserId={job.details.jobManager.userId}
                            contactInitials={job.details.jobManager.initials}
                            contactPhoneNumber={
                                job.details.jobManager.phoneNumber
                            }
                            contactProfileImageUrl={
                                job.details.jobManager.profileImageUrl
                            }
                            actionableLinks={true}
                            isClient={true}
                        />
                    </Breakpoint>

                    {job.details.externalDescription ? (
                        <InternalNotes
                            title={"imported description"}
                            jobDescription={textWithLinks(
                                job.details.externalDescription
                            )}
                        />
                    ) : null}
                </div>
            </div>
        </LayoutContainer>
    );
};
